import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { values } from 'lodash';

import styles from './TaxWithholding.module.scss';

import DestinationOption from '../../components/DestinationOption/DestinationOption';
import HelpBox from '../../components/HelpBox/HelpBox';
import PageWrapper from '../../components/PageWrapper/PageWrapper';
import ProgressButtons from '../../components/Progress/ProgressButtons/ProgressButtons';
import ProgressBar from '../../components/Progress/ProgressBar/ProgressBar';
import RMLConnection from '../../components/RMLConnection/RMLConnection';
import RmdInputBox from '../../components/Shared/RmdInputBox/RmdInputBox';
import { getWithholdingStateLookup } from '../../redux/modules/stateWithholding/stateWithholding.selectors';
import {
  getTaxWithholding,
  getSelectedDistributionFunds,
  getSelectedDistribution,
} from '../../redux/modules/shoppingCart/shoppingCart.selectors';
import {
  TAX_WITHHOLDING_STEP,
  TOLL_FREE_NUMBER,
  TAX_WITHHOLDING_NOTICE_STEP,
  FEDERAL_WITHOLDINGS,
  STATE_CODES,
  REVIEW_SUBMIT_STEP,
  SPECIAL_STATES,
  FREQUENCY_STEP,
} from '../../settings/settings';
import {
  checkForWithholdingState,
  multiHoldingState,
} from '../../../src/utils/fund-utils';
import TaxWithholdingNotes from '../../components/TaxWithholdingNotes/TaxWithholdingNotes';
import {
  openModal as openModalAction,
  STATES_DISCLOSURE_NOTES_MODAL,
  WITHHOLDING_DISCLOSURE_MODAL,
} from '../../redux/modules/modals/modals.module';
import { getSalesforceRep } from '../../redux/modules/applicationConfig/applicationConfig.selectors';
import { getUserAddress } from '../../redux/modules/PersonalInformation/personalInformation.selectors';
import { getMandatoryWithholdingStates } from '../../redux/modules/stateWithholding/stateWithholding.selectors';
const { NO_SELECTION, ELECT_FEDERAL_WITHHOLDING, DECLINE_FEDERAL_WITHHOLDING } =
  FEDERAL_WITHOLDINGS;

const TaxWithholding = (props) => {
  const selectedOption = useSelector(getTaxWithholding);
  const selectedFunds = useSelector(getSelectedDistributionFunds);
  const isSalesforceRep = useSelector((state) => getSalesforceRep(state));
  const [isRepSelected, setIsRepSelected] = useState(false);
  const dispatch = useDispatch();
  const stableDispatch = useCallback(dispatch, []);
  useEffect(() => {
    if (!selectedOption.selected) {
      const modalName = WITHHOLDING_DISCLOSURE_MODAL;
      stableDispatch(openModalAction(modalName));
    }
  }, [stableDispatch, selectedOption]);

  const [state, setState] = useState({
    selected: selectedOption.selected
      ? selectedOption.selected
      : ELECT_FEDERAL_WITHHOLDING,
    defaultPlaceHolder: selectedOption.value
      ? selectedOption.value
      : selectedOption.selected === DECLINE_FEDERAL_WITHHOLDING
      ? ''
      : '10',
    hasError: false,
    value: selectedOption.value
      ? selectedOption.value
      : selectedOption.selected === DECLINE_FEDERAL_WITHHOLDING
      ? null
      : 10,
  });

  const electWithholding = () => {
    if (state.selected !== ELECT_FEDERAL_WITHHOLDING)
      setState({
        ...state,
        selected: ELECT_FEDERAL_WITHHOLDING,
        defaultPlaceHolder: 10,
        value: 10,
      });
  };

  const declineWithholding = () => {
    setState({
      ...state,
      selected: DECLINE_FEDERAL_WITHHOLDING,
      defaultPlaceHolder: '0',
      value: 0,
      hasError: false,
    });
  };
  const distribution = useSelector(getSelectedDistribution);
  const statePercentLookUp = useSelector(getWithholdingStateLookup);
  const allSelectedFunds = (
    selectedFunds.traditionalFunds
      ? values(selectedFunds.traditionalFunds).filter(
          (fund) =>
            fund.value > 0 ||
            distribution === 'PROPORTIONATE_DISTRIBUTION_METHOD_STEP'
        )
      : []
  ).concat(
    selectedFunds.rolloverFunds
      ? values(selectedFunds.rolloverFunds).filter(
          (fund) =>
            fund.value > 0 ||
            distribution === 'PROPORTIONATE_DISTRIBUTION_METHOD_STEP'
        )
      : []
  );
  const multiHoldingStates = multiHoldingState(
    allSelectedFunds,
    statePercentLookUp,
    STATE_CODES
  );

  const withHoldingState = checkForWithholdingState(
    STATE_CODES,
    allSelectedFunds
  );
  let percentage;
  let mandatory;
  let multiState;
  if (multiHoldingStates && multiHoldingStates.multiState) {
    multiState = multiHoldingStates.multiState;
    mandatory = multiHoldingStates.mandatory;
  } else {
    if (withHoldingState) {
      const stateCode = withHoldingState.stateCode;
      if (statePercentLookUp[stateCode]) {
        percentage = statePercentLookUp[stateCode].percent;
        mandatory = statePercentLookUp[stateCode].mandatory;
      }
    }
  }
  const electWithHoldingTaxNote =
    state.selected === ELECT_FEDERAL_WITHHOLDING ||
    (state.selected !== ELECT_FEDERAL_WITHHOLDING &&
      SPECIAL_STATES.indexOf(withHoldingState && withHoldingState.stateCode) >
        -1);

  const declineWithHoldingTaxNote =
    state.selected === DECLINE_FEDERAL_WITHHOLDING &&
    SPECIAL_STATES.indexOf(withHoldingState && withHoldingState.stateCode) ===
      -1;

  const handleClick = (stateType) => {
    const modalName = STATES_DISCLOSURE_NOTES_MODAL;
    const stateNameList = multiState
      ? multiHoldingStates.stateList
      : [
          {
            stateName: withHoldingState.stateName,
            stateCode: withHoldingState.stateCode,
          },
        ];
    const percentList = multiState
      ? multiHoldingStates.stateList
      : [{ percentage: percentage }];
    stableDispatch(
      openModalAction(modalName, {
        stateType: stateType,
        stateName: stateNameList,
        percentage: percentList,
      })
    );
  };
  const isWithholdOptionChanged = state.value !== selectedOption.value;
  return (
    <div className="rmd-distribution-wrapper">
      <div className="sticky-header">
        <div className="heading">Auto-RMD Setup</div>
        <ProgressButtons
          edit={props.edit}
          currentStep={TAX_WITHHOLDING_STEP}
          previousStep={FREQUENCY_STEP}
          currentSelection={selectedOption}
          payload={{
            selected: state.selected,
            value: state.value,
            multiState: multiState,
            mandatory: mandatory,
            percentage: percentage,
            stateList: multiHoldingStates.stateList,
            withHoldingState: withHoldingState,
          }}
          nextStep={REVIEW_SUBMIT_STEP}
          showBackButton={true}
          backDisabled={false}
          isWithholdOptionChanged={isWithholdOptionChanged}
          disabled={
            state.selected === NO_SELECTION ||
            (!isRepSelected && isSalesforceRep)
          }
          {...props}
        >
          <ProgressBar completedSteps={[0, 1, 2]} activeIndex={3} />
        </ProgressButtons>
      </div>
      <PageWrapper nextStep={TAX_WITHHOLDING_STEP} {...props}>
        <div className="scroll-wrapper">
          <div className="rmd-title">
            Do you want to withhold{' '}
            <span className="orphan-line">federal income taxes?</span>
          </div>
          <div className={styles['option-container']}>
            <div id="electWithholding" onClick={electWithholding}>
              <DestinationOption
                withHoldingError={state.hasError}
                withHolding={true}
                optionText={
                  <div className={styles['withholding-container']}>
                    <div className={styles['elect-withholding']}>
                      <span className="emphasis">Yes,&nbsp;</span> Withhold:
                      <div className={styles['input']}>
                        <RmdInputBox
                          handleValueChange={(value) => {
                            const num = parseInt(Number(value), 10);
                            const hasError = num > 99 || num < 0;
                            if (num === 0) {
                              declineWithholding();
                            } else if (state.selected !== DECLINE_FEDERAL_WITHHOLDING) {
                            setState({
                              ...state,
                              value: num,
                              hasError: hasError,
                              selected: !hasError
                                ? ELECT_FEDERAL_WITHHOLDING
                                : NO_SELECTION,
                            });
                          }}}
                          defaultPlaceHolder={state.defaultPlaceHolder}
                          defaultValue={state.value}
                          changePlaceHolder={state.defaultPlaceHolder}
                          hasError={state.hasError}
                          openBox
                          intOnly
                        />
                      </div>
                    </div>
                    <br />
                    <div className={styles['withholding-disclaimer']}>
                      Edit this field if you would like a rate of withholding
                      that is different from the default withholding rate. See
                      the instructions on page 2 of the IRS Form W-4R and the
                      Marginal Rate Tables for additional information. Enter the
                      rate as a whole number (no decimals). The following link
                      will take you to the{' '}
                      <a
                        href={`https://www.irs.gov/pub/irs-pdf/fw4r.pdf`}
                        rel="noreferrer"
                        target="_blank"
                      >
                        {new Date().getFullYear()} Marginal Rate Tables
                      </a>
                      . You may use these tables to help you select the
                      appropriate withholding rate for this payment or
                      distribution. Instructions how to best use them are
                      included.
                      {/* Must be between {FederalTaxPercent.MIN}&ndash;
                      {FederalTaxPercent.MAX}% or 0% */}
                    </div>
                  </div>
                }
                selectable={true}
                isSelected={state.selected === ELECT_FEDERAL_WITHHOLDING}
              />
            </div>
            <div id="declineWithholding" onClick={declineWithholding}>
              <DestinationOption
                optionText={
                  <div className={styles['no-withholding-container']}>
                    <span className="emphasis">No, </span>Do Not Withhold
                  </div>
                }
                selectable={true}
                isSelected={state.selected === DECLINE_FEDERAL_WITHHOLDING}
              />
            </div>
            {(withHoldingState || multiState) && (
              <div className={styles['tax-div']}>
                {(electWithHoldingTaxNote ||
                  declineWithHoldingTaxNote ||
                  multiState) && (
                  <>
                    <TaxWithholdingNotes
                      withHoldingState={withHoldingState}
                      percentage={percentage}
                      mandatory={mandatory}
                      multiState={multiState}
                      stateList={multiHoldingStates.stateList}
                      declineWithHolding={declineWithHoldingTaxNote}
                      electWithHoldingTaxNote={electWithHoldingTaxNote}
                      handleClick={handleClick}
                    />
                  </>
                )}
                <div></div>
              </div>
            )}
            <HelpBox step={TAX_WITHHOLDING_NOTICE_STEP} openBox={true} />
            <RMLConnection phoneNumber={TOLL_FREE_NUMBER} />
            {isSalesforceRep && (
              <div className={styles['availaibilty-check-con']}>
                <label>
                  <input
                    type="checkbox"
                    value={isRepSelected}
                    className={styles['acknowledgement-box']}
                    onChange={() => setIsRepSelected(!isRepSelected)}
                  />
                  <span className={styles['availiblity-check-text']}>
                    I have read the current{' '}
                    <a
                      href="https://home2.troweprice.com/discovery/confluence/display/retailkms/Templates+And+Disclosures#TemplatesAndDisclosures-IRARedemptionDisclosure"
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      Federal Witholding Disclosures
                    </a>
                    . Note: Disclosure must be read whether federal withholding
                    elected or not
                  </span>
                </label>
              </div>
            )}
          </div>
        </div>
      </PageWrapper>
    </div>
  );
};
export default TaxWithholding;
